import { http, constants } from 'lib_ui-services';
import {
    state,
    display,
    isCurrent,
    remember,
    syncAppearance,
    presentation,
    launchAppearance
} from './session';
import connector from './connector';
import reviewReaders from './reviewReaders';
import provisionReaders from './provisionReaders';
export const _private = {
    http,
    helperRequest: connector.request,
    origin: connector.origin,
    openHelper: connector.open,
    forgetHelper: connector.forget,
    download: connector.download,
    wait: () => new Promise(resolve => setTimeout(resolve, 2000))
};
export default {
    verb: 'doingCreate',
    namespace: 'application',
    relation: 'readerSetup',
    description: 'Pair the local helper and review reader setup',
    logic: run
};
async function run({ data, context, dispatch }) {
    if (!context.type?.startsWith('readerSetup.')) return;
    const setup = state(context);
    const action = context.type.slice('readerSetup.'.length);
    if ((setup.pending && action !== 'end') || data.record?._id !== setup.draft._id) return;
    setup.pending = true;
    const requestId = (setup.requestId = (setup.requestId || 0) + 1);
    setup.formInstanceId = context.formInstanceId || setup.formInstanceId;
    const base = `/g/${context.session.groupNumber || 0}/api/deviceManagement/device/setup`;
    const active = () => isCurrent(setup) && setup.requestId === requestId;
    const show = async draft => {
        if (!active()) return;
        draft = presentation({ ...draft, busy: setup.pending, mqttLoginReady: !!setup.mqttLoginReady });
        setup.draft = draft;
        await dispatch(
            { record: draft, ignoreChanges: true },
            {
                verb: 'edit',
                namespace: 'application',
                relation: 'readerSetup',
                type: 'singleRecordForm',
                formInstanceId: context.formInstanceId,
                formRefreshInstanceId: context.formInstanceId
            }
        );
    };
    // Only status reads are retried. Repeating a write could reconnect a reader twice.
    const waitFor = async (url, ready, attempts = 65, required = true) => {
        for (let attempt = 0; attempt < attempts && active(); attempt++) {
            await _private.wait();
            if (!active()) return;
            let snapshot;
            try {
                snapshot = await _private.http.get(url);
            } catch (error) {
                if (!error.status || [502, 503, 504].includes(error.status)) continue;
                throw error;
            }
            if (!active()) return;
            await show(display(setup.draft, snapshot));
            if (ready(snapshot)) return snapshot;
        }
        if (active() && required)
            throw new Error(`Still waiting. ${connector.keepOpenMessage} Reopen this page to check progress.`);
    };
    try {
        let draft = { ...setup.draft, busy: true, requestStatus: '' };
        if (action === 'connectionStatus') {
            await new Promise(resolve => dispatch({
                title: 'Reader connection',
                message: draft.paired
                    ? `Connected and ready to communicate with readers on your network. ${connector.keepOpenMessage} Use the menu to disconnect.`
                    : connector.connectingMessage,
                okButtonText: 'OK', cancelButtonText: 'Close',
                okAction: resolve, cancelAction: resolve
            }, { verb: 'confirm', namespace: 'application', relation: 'user' }));
            return;
        } else if (action === 'retry') {
            const failed = (draft.readerProgress || []).filter(row => row.status === 'Needs attention');
            if (!failed.length) return;
            draft.readerChoices = failed.map(row => ({ _id: row._id }));
            draft.readerAddress = '';
            draft.credentialsRequested = true;
            draft.requestStatus = 'Retry only the readers that need attention. Leave the password blank to reuse the login for this session.';
        } else if (action.startsWith('download')) {
            if (!_private.download) throw new Error('Reader Connector downloads are available in the browser.');
            const downloads = await _private.http.get(`${base}/downloads`);
            if (!active()) return;
            const url = downloads[action.slice('download'.length)];
            if (!url) throw new Error('Reader Connector downloads are not available yet. Contact support.');
            _private.download(url);
            draft.requestStatus = 'Install Reader Connector, then choose Find readers here.';
        } else if (action === 'manualAddress' && draft.paired) {
            draft.manualAddress = true;
        } else if (action === 'cancelManualAddress') {
            draft = {
                ...draft,
                manualAddress: false,
                readerAddress: '',
                hasReadersSelected: !!draft.readerChoices?.length,
                credentialsRequested: false,
                readerPassword: '',
                mqttPassword: '',
                needsRegion: false,
                advancedRegion: false,
                readyToConnect: false
            };
        } else if (action === 'credentials') {
            if (!presentation({ ...draft, busy: false }).canProvision) return;
            draft.credentialsRequested = true;
            draft.requestStatus =
                'Enter the login for the selected readers, then choose Continue to check them.';
        } else if (action === 'cancelCredentials') {
            draft = { ...draft, credentialsRequested: false, readerPassword: '', mqttPassword: '', requestStatus: '' };
        } else if (action === 'start' || action === 'manualAddress' || action === 'discover') {
            let snapshot;
            if (setup.id) {
                snapshot = await _private.http.get(`${base}/${setup.id}`);
                if (!active()) return;
                if (snapshot.paired) {
                    syncAppearance(setup);
                    if (action !== 'discover')
                        await _private.openHelper('backbone-reader-setup://open', launchAppearance());
                    draft = display(draft, snapshot);
                } else if (setup.pairingLink && setup.pairingExpiresAt > Date.now()) {
                    await _private.openHelper(setup.pairingLink, launchAppearance());
                } else {
                    await _private.http.remove(`${base}/${setup.id}`);
                    if (!active()) return;
                    setup.id = undefined;
                }
            }
            if (!setup.id) {
                const pair = await _private.http.post(base, { mqttUsername: draft.mqttUsername });
                if (!active()) return;
                setup.id = pair.id;
                setup.pairingLink = `backbone-reader-setup://pair#${encodeURIComponent(JSON.stringify({ url: `${_private.origin()}${base}`, ...pair }))}`;
                setup.pairingExpiresAt = Date.now() + 5 * 60 * 1000;
                remember(setup);
                syncAppearance(setup);
                await _private.openHelper(setup.pairingLink, launchAppearance());
            }
            if (!snapshot?.paired) {
                await show({
                    ...draft,
                    connectionStatus: 'Waiting for Reader Connector',
                    requestStatus: connector.connectingMessage
                });
                snapshot = await waitFor(`${base}/${setup.id}`, next => next.paired);
            }
            if (!active()) return;
            if (action === 'manualAddress') {
                draft = { ...display(setup.draft, snapshot), manualAddress: true, requestStatus: '' };
            } else {
                await show({ ...display(setup.draft, snapshot), requestStatus: 'Finding readers on this network…' });
                snapshot = await _private.helperRequest(
                    `${base}/${setup.id}`,
                    snapshot,
                    'discover',
                    action === 'discover' && draft.networkAddress ? { address: draft.networkAddress } : {},
                    active
                );
                if (!active()) return;
                draft = discovered(display(setup.draft, snapshot), snapshot.command.result);
            }
        } else {
            if (!setup.id) throw new Error('Choose Find readers first.');
            const url = `${base}/${setup.id}`;
            if (action === 'end') {
                try {
                    await _private.http.remove(url);
                } catch (error) {
                    // The helper may already have ended the session, or it may have expired.
                    // Clear the form in either case so support can start another setup.
                    if (error.status !== 404) throw error;
                }
                await _private.forgetHelper();
                if (!active()) return;
                setup.id = undefined;
                setup.pairingLink = undefined;
                setup.pairingExpiresAt = undefined;
                setup.standards = undefined;
                setup.mqttLoginReady = false;
                draft = {
                    _id: draft._id,
                    mqttUsername: draft.mqttUsername,
                    readerUsername: 'admin',
                    paired: false,
                    needsRegion: false,
                    advancedRegion: false,
                    readyToConnect: false,
                    hasReadersSelected: false,
                    connectionStatus: 'Connector disconnected'
                };
            } else {
                syncAppearance(setup);
                let snapshot = await _private.http.get(url);
                if (!active()) return;
                draft = display(draft, snapshot);
                if (action === 'refresh') {
                    draft.requestStatus =
                        draft.readerProgress.some(row => row.status === 'Provisioning')
                            ? connector.provisioningMessage
                            : draft.readerProgress.some(row => row.status === 'Needs attention')
                              ? draft.batchProgress
                              : draft.readerStatus === 'Connected'
                              ? 'Connected. You can select more readers to provision or finish setup.'
                              : draft.readerError || draft.readerStatus;
                }
                if (action === 'selectRegion') {
                    const standard = setup.standards?.find(row => row.standardName === draft.standardName);
                    if (
                        setup.standardsReader !== draft.readerId ||
                        setup.standardsRegion !== draft.regionName ||
                        !standard
                    )
                        throw new Error('Load the supported standards and choose one first.');
                    const input = {
                        id: draft.readerId,
                        region: {
                            regionName: draft.regionName,
                            standardName: draft.standardName,
                            channels: standard.channelSelection
                                ? String(draft.regionChannels || '')
                                      .split(',')
                                      .map(value => Number(value.trim()))
                                : [],
                            doLBT: standard.lbtConfigurable ? !!draft.doLBT : null,
                            doHopping: standard.hoppingConfigurable ? !!draft.doHopping : null
                        }
                    };
                    // Refresh the existing form so both updates keep the same form identity.
                    // Clear password fields before awaiting the helper. Credentials are reused
                    // in its memory; this temporary form does not retain submitted passwords.
                    draft = {
                        ...draft,
                        readerPassword: '',
                        mqttPassword: '',
                        requestStatus: 'Waiting for Reader Connector…'
                    };
                    setup.draft = draft;
                    await dispatch(
                        { record: draft, ignoreChanges: true },
                        {
                            verb: 'edit',
                            namespace: 'application',
                            relation: 'readerSetup',
                            type: 'singleRecordForm',
                            formInstanceId: context.formInstanceId,
                            formRefreshInstanceId: context.formInstanceId
                        }
                    );
                    snapshot = await _private.helperRequest(url, snapshot, action, input, active);
                    if (!active()) return;
                    draft = display(draft, snapshot);
                    draft.requestStatus = 'Done';
                } else if (action === 'review') {
                    ({ draft, snapshot } = await reviewReaders({
                        draft, snapshot, setup, url, active, show, waitFor, dispatch, services: _private
                    }));
                } else if (action === 'connect') {
                    draft = await provisionReaders({
                        draft, snapshot, setup, url, active, show, waitFor, dispatch, services: _private
                    });
                    if (!draft) return;
                } else if (action !== 'refresh') throw new Error('Unknown reader setup action.');
            }
        }
        if (!active()) return;
        setup.draft = draft;
        remember(setup);
    } catch (error) {
        if (active()) {
            if (error.status === 404) {
                setup.id = undefined;
                setup.mqttLoginReady = false;
                remember(setup);
            }
            setup.draft = { ...setup.draft, requestStatus: error.message };
            await dispatch(
                { message: error.message, variant: 'error', isError: true, type: constants.notificationTypes.TOAST },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        }
        throw error;
    } finally {
        if (active()) {
            // Publish completion only after clearing busy. Back-to-back form refreshes
            // can overlap in ActiveRecord and leave the earlier busy record displayed.
            setup.pending = false;
            await show({ ...setup.draft, checkingAddress: '' });
        }
    }
}

function discovered(draft, result) {
    const candidates = result.candidates.map(row => ({
        _id: row.address,
        title: (row.hostname || row.address).toLowerCase(),
        address: row.address,
        hostname: row.hostname,
        registration: row.registration
    }));
    // Keep progress visible across scans, including readers that stop answering discovery.
    const byAddress = new Map((draft.readerProgress || []).map(row => [row._id, row]));
    candidates.forEach(row => byAddress.set(row._id, { status: 'Discovered', statusColor: 'Cool Gray', ...byAddress.get(row._id), ...row }));
    const allConnected =
        candidates.length > 0 && candidates.every(row => byAddress.get(row._id).status === 'Connected');
    const message = allConnected
        ? `${candidates.length === 1 ? 'This reader is' : 'These readers are'} already connected. No provisioning is needed. Use Find readers to look for additional readers.`
        : candidates.length
          ? `${candidates.length} reader${candidates.length === 1 ? '' : 's'} found. Select the readers you want to provision.`
          : 'No readers found. Check that this computer and reader are on the same network, or enter its address.';
    return {
        ...draft,
        candidates,
        readerProgress: [...byAddress.values()],
        discoveredReaders: message,
        requestStatus: message
    };
}
